import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { TradePosition, PositionStatus } from '@covered-calls/shared';
import { calculateRealisedPnL, calculatePnLBreakdown, roundTo2dp } from '@covered-calls/shared';
import { apiClient, ApiClientError } from '../services/api-client';
import { getIbkrConnection } from '../services/ibkr-settings-service';
import { useAutoRefresh } from '../hooks/useAutoRefresh';

/**
 * TradeList displays open/closed positions with filtering and sorting.
 * Click a row to navigate to the position detail view.
 *
 * Validates: Requirements 4.2, 9.2, 9.5, 15.2
 */

type FilterTab = 'ALL' | 'OPEN' | 'CLOSED';
type SortField = 'tradeDate' | 'ticker' | 'status' | 'unrealisedPnL';
type SortDirection = 'asc' | 'desc';

export function TradeList() {
  const navigate = useNavigate();
  const [positions, setPositions] = useState<TradePosition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterTab>('OPEN');
  const [sortField, setSortField] = useState<SortField>('tradeDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [refreshFailureCount, setRefreshFailureCount] = useState(0);
  // Delete-confirmation flow: the position pending deletion (null = none).
  const [deleteTarget, setDeleteTarget] = useState<TradePosition | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const fetchPositions = useCallback(async (filter: FilterTab) => {
    setIsLoading(true);
    setError(null);
    try {
      const queryParam = filter !== 'ALL' ? `?status=${filter}` : '';
      const data = await apiClient.get<TradePosition[]>(`/trades${queryParam}`);
      setPositions(data);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('Failed to load positions. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPositions(activeFilter);
  }, [activeFilter, fetchPositions]);

  useEffect(() => {
    // Whether automated price fetching is on. When enabled, all positions
    // refresh together on the feed cycle; when disabled, the user updates
    // positions individually. Non-critical: failures leave it disabled.
    const loadAutoRefresh = async () => {
      try {
        const conn = await getIbkrConnection();
        setAutoRefreshEnabled(conn?.enabled ?? false);
      } catch {
        setAutoRefreshEnabled(false);
      }
    };
    loadAutoRefresh();
  }, []);

  const handleFilterChange = (tab: FilterTab) => {
    setActiveFilter(tab);
  };

  // Background refresh — does NOT set isLoading so there is no layout flash.
  const backgroundRefreshFetch = useCallback(async () => {
    const queryParam = activeFilter !== 'ALL' ? `?status=${activeFilter}` : '';
    const data = await apiClient.get<TradePosition[]>(`/trades${queryParam}`);
    setPositions(data);
    setRefreshFailureCount(0); // reset on success
  }, [activeFilter]);

  useAutoRefresh({
    fetchFn: backgroundRefreshFetch,
    enabled: autoRefreshEnabled,
    onError: (count) => setRefreshFailureCount(count),
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedPositions = [...positions].sort((a, b) => {
    const direction = sortDirection === 'asc' ? 1 : -1;
    switch (sortField) {
      case 'tradeDate':
        return direction * (a.tradeDate ?? '').localeCompare(b.tradeDate ?? '');
      case 'ticker':
        return direction * (a.ticker ?? '').localeCompare(b.ticker ?? '');
      case 'status':
        return direction * (a.status ?? '').localeCompare(b.status ?? '');
      case 'unrealisedPnL': {
        const aPnL = a.unrealisedPnL ?? a.realisedPnL ?? 0;
        const bPnL = b.unrealisedPnL ?? b.realisedPnL ?? 0;
        return direction * (aPnL - bPnL);
      }
      default:
        return 0;
    }
  });

  const handleRowClick = (positionId: string) => {
    navigate(`/trades/${positionId}`);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await apiClient.delete(`/trades/${deleteTarget.positionId}`);
      setDeleteTarget(null);
      await fetchPositions(activeFilter);
    } catch (err) {
      setDeleteError(
        err instanceof ApiClientError ? err.message : 'Failed to delete the trade. Please try again.'
      );
    } finally {
      setIsDeleting(false);
    }
  };

  // Open positions show an "income to date" P&L that EXCLUDES the current
  // active-call buyback liability (the cost of closing the short call is only
  // incurred if it is actually bought back/rolled). This keeps the list P&L
  // consistent with the Dashboard's Net P&L and the Position Detail headline.
  // `calculateUnrealisedPnL` by contrast is the mark-to-market figure (it
  // subtracts that liability) — intentionally NOT used for the column here.
  // Derived display-only from the shared breakdown; no shared-calc change.
  const getPositionPnL = (position: TradePosition): number | undefined => {
    if (position.status === 'CLOSED') {
      // Realised P&L correctly includes the actual buyback cost paid at exit.
      return position.realisedPnL ?? calculateRealisedPnL(position);
    }
    // Income to date = breakdown total minus the active-call liability line.
    // Undefined (→ 'N/A') is preserved when market data is missing, matching
    // the previous calculateUnrealisedPnL behaviour.
    const breakdown = calculatePnLBreakdown(position);
    if (breakdown === undefined) {
      return undefined;
    }
    return roundTo2dp(breakdown.netProfitLoss - breakdown.netRollCostCredit);
  };

  const formatPnL = (position: TradePosition): string => {
    const pnl = getPositionPnL(position);
    if (pnl === undefined) return 'N/A';
    return `$${pnl.toFixed(2)}`;
  };

  const getPnLColor = (position: TradePosition): string => {
    const pnl = getPositionPnL(position);
    if (pnl === undefined) return 'var(--color-muted)';
    if (pnl > 0) return 'var(--color-pnl-positive)';
    if (pnl < 0) return 'var(--color-pnl-negative)';
    return 'var(--color-muted)';
  };

  const getSortIndicator = (field: SortField): string => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' \u2191' : ' \u2193';
  };

  // Most recent market-data refresh across the loaded positions. Reflects the
  // last time prices were updated (manually or from the feed).
  const lastUpdated = positions.reduce<string | undefined>((latest, p) => {
    if (!p.lastPriceTimestamp) return latest;
    if (!latest || p.lastPriceTimestamp > latest) return p.lastPriceTimestamp;
    return latest;
  }, undefined);

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h2 style={{ margin: 0 }}>Positions</h2>
        {autoRefreshEnabled && (
          <span aria-live="polite" aria-atomic="true" style={{ fontSize: '0.8rem', color: 'var(--color-muted)' }}>
            {lastUpdated
              ? `Prices last updated ${new Date(lastUpdated).toLocaleString()}`
              : 'Prices not yet updated'}
          </span>
        )}
      </div>

      {autoRefreshEnabled && refreshFailureCount >= 3 && (
        <div role="status" style={{ fontSize: '0.8rem', color: 'var(--color-muted)' }}>
          Prices could not be refreshed. Displaying last known data.
        </div>
      )}

      {/* Filter tabs */}
      <nav aria-label="Position filter" style={{ marginBottom: '1rem' }}>
        <div role="tablist" style={{ display: 'flex', gap: '0.25rem', borderBottom: '2px solid var(--color-border)' }}>
          {(['ALL', 'OPEN', 'CLOSED'] as FilterTab[]).map((tab) => (
            <button
              key={tab}
              role="tab"
              aria-selected={activeFilter === tab}
              onClick={() => handleFilterChange(tab)}
              style={{
                padding: '0.5rem 1rem',
                border: 'none',
                borderBottom: activeFilter === tab ? '2px solid var(--color-brand)' : '2px solid transparent',
                backgroundColor: 'transparent',
                color: activeFilter === tab ? 'var(--color-brand)' : 'var(--color-muted)',
                fontWeight: activeFilter === tab ? 600 : 400,
                cursor: 'pointer',
                minHeight: '44px',
                marginBottom: '-2px',
              }}
            >
              {tab === 'ALL' ? 'All' : tab === 'OPEN' ? 'Open' : 'Closed'}
            </button>
          ))}
        </div>
      </nav>

      {/* Error state */}
      {error && (
        <div role="alert" style={{ padding: '1rem', backgroundColor: 'var(--color-chip-danger-bg)', color: 'var(--color-chip-danger-text)', borderRadius: '4px', marginBottom: '1rem' }}>
          <p style={{ margin: 0 }}>{error}</p>
          <button
            onClick={() => fetchPositions(activeFilter)}
            style={{ marginTop: '0.5rem', padding: '0.5rem 1rem', backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)', border: 'none', borderRadius: '4px', cursor: 'pointer', minHeight: '44px' }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div aria-live="polite" style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-muted)' }}>
          Loading positions...
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && sortedPositions.length === 0 && (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-muted)' }}>
          <p>No positions found{activeFilter !== 'ALL' ? ` with status "${activeFilter.toLowerCase()}"` : ''}.</p>
        </div>
      )}

      {/* Positions table */}
      {!isLoading && !error && sortedPositions.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }} aria-label="Trade positions">
            <thead>
              <tr style={{ borderBottom: '2px solid var(--color-border)', textAlign: 'left' }}>
                <th style={{ padding: '0.75rem 0.5rem' }}>
                  <button
                    onClick={() => handleSort('ticker')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0, fontSize: 'inherit', minHeight: 'auto' }}
                    aria-label={`Sort by ticker${getSortIndicator('ticker')}`}
                  >
                    Ticker{getSortIndicator('ticker')}
                  </button>
                </th>
                <th style={{ padding: '0.75rem 0.5rem' }}>
                  <button
                    onClick={() => handleSort('tradeDate')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0, fontSize: 'inherit', minHeight: 'auto' }}
                    aria-label={`Sort by trade date${getSortIndicator('tradeDate')}`}
                  >
                    Trade Date{getSortIndicator('tradeDate')}
                  </button>
                </th>
                <th style={{ padding: '0.75rem 0.5rem' }}>Type</th>
                {activeFilter === 'ALL' && (
                  <th style={{ padding: '0.75rem 0.5rem' }}>
                    <button
                      onClick={() => handleSort('status')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0, fontSize: 'inherit', minHeight: 'auto' }}
                      aria-label={`Sort by status${getSortIndicator('status')}`}
                    >
                      Status{getSortIndicator('status')}
                    </button>
                  </th>
                )}
                <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>
                  <button
                    onClick={() => handleSort('unrealisedPnL')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0, fontSize: 'inherit', minHeight: 'auto' }}
                    aria-label={`Sort by P&L${getSortIndicator('unrealisedPnL')}`}
                  >
                    P&L{getSortIndicator('unrealisedPnL')}
                  </button>
                </th>
                {!autoRefreshEnabled && activeFilter !== 'CLOSED' && (
                  <th style={{ padding: '0.75rem 0.5rem' }}>Last Updated</th>
                )}
                <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Actions</th>

              </tr>
            </thead>
            <tbody>
              {sortedPositions.map((position) => (
                <tr
                  key={position.positionId}
                  onClick={() => handleRowClick(position.positionId)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleRowClick(position.positionId);
                    }
                  }}
                  tabIndex={0}
                  role="row"
                  aria-label={`${position.ticker} position, ${position.status.toLowerCase()}`}
                  style={{
                    borderBottom: '1px solid var(--color-border)',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s',
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-bg-alt)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                >
                  <td style={{ padding: '0.75rem 0.5rem', fontWeight: 600 }}>
                    {position.ticker}
                  </td>
                  <td style={{ padding: '0.75rem 0.5rem' }}>
                    {new Date(position.tradeDate).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '0.75rem 0.5rem' }}>
                    {position.tradeType === 'BUY_WRITE'
                      ? 'Buy/Write'
                      : position.tradeType === 'PROTECTIVE_PUT'
                        ? 'Protective Put'
                        : 'Separate'}
                  </td>
                  {activeFilter === 'ALL' && (
                    <td style={{ padding: '0.75rem 0.5rem' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '0.125rem 0.5rem',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          backgroundColor: position.status === 'OPEN' ? 'var(--color-chip-success-bg)' : 'var(--color-surface-2)',
                          color: position.status === 'OPEN' ? 'var(--color-chip-success-text)' : 'var(--color-muted)',
                        }}
                      >
                        {position.status}
                      </span>
                    </td>
                  )}
                  <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', fontWeight: 500, color: getPnLColor(position) }}>
                    {formatPnL(position)}
                  </td>
                  {!autoRefreshEnabled && activeFilter !== 'CLOSED' && (
                    <td style={{ padding: '0.75rem 0.5rem', fontSize: '0.85rem', color: 'var(--color-muted)' }}>
                      {position.lastPriceTimestamp
                        ? new Date(position.lastPriceTimestamp).toLocaleString()
                        : '\u2014'}
                    </td>
                  )}
                  <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setDeleteError(null); setDeleteTarget(position); }}
                      aria-label={`Delete ${position.ticker} trade`}
                      style={{
                        padding: '0.25rem 0.6rem',
                        fontSize: '0.75rem',
                        background: 'var(--color-surface)',
                        color: 'var(--color-danger)',
                        border: '1px solid var(--color-danger)',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        minHeight: 'auto',
                      }}
                    >
                      Delete
                    </button>
                  </td>

                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete confirmation dialog */}
      {deleteTarget && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-trade-title"
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            padding: '1rem',
          }}
          onClick={() => { if (!isDeleting) setDeleteTarget(null); }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--color-surface)',
              borderRadius: '8px',
              padding: '1.5rem',
              maxWidth: '440px',
              width: '100%',
              boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
            }}
          >
            <h3 id="delete-trade-title" style={{ margin: '0 0 0.5rem' }}>
              Delete {deleteTarget.ticker} trade?
            </h3>
            <p style={{ color: 'var(--color-muted)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
              This permanently deletes the position and all of its related data —
              option and roll history, and any uploaded trade reports. This cannot
              be undone.
            </p>
            {deleteError && (
              <div role="alert" style={{ color: 'var(--color-chip-danger-text)', backgroundColor: 'var(--color-chip-danger-bg)', borderRadius: '4px', padding: '0.5rem 0.75rem', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                {deleteError}
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={isDeleting}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'var(--color-surface)',
                  color: 'var(--color-muted)',
                  border: '1px solid var(--color-input-border)',
                  borderRadius: '4px',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  minHeight: '44px',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={isDeleting}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'var(--color-danger)',
                  color: 'var(--color-on-brand)',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  minHeight: '44px',
                  opacity: isDeleting ? 0.7 : 1,
                }}
              >
                {isDeleting ? 'Deleting...' : 'Delete Trade'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
